<template>
    <div class="tw-px-4">
      <!-- Loading skeleton -->
      <div v-if="loading" class="tw-bg-white tw-p-5 tw-flex tw-flex-col tw-gap-3">
        <div class="tw-h-4 tw-w-full tw-bg-gray-100 tw-animate-pulse tw-rounded"></div>
        <div class="tw-h-8 tw-w-full tw-bg-gray-100 tw-animate-pulse tw-rounded"></div>
        <div class="tw-h-4 tw-w-full tw-bg-gray-100 tw-animate-pulse tw-rounded"></div>
      </div>
  
      <!-- Activity timeline -->
      <div v-else class="tw-bg-white tw-p-5">
        <ol class="tw-relative tw-border-l tw-border-gray-300">
          <li
            v-for="entry in data"
            :key="entry.id"
            class="tw-mb-6 tw-ml-6"
          >
            <!-- Icon -->
            <span
              :class="[
                'tw-absolute tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-full -tw-left-3 tw-translate-y-1/2',
                getIcon(entry).bg
              ]"
            >
              <icon :icon="getIcon(entry).name" class="tw-w-4 tw-h-4 tw-text-white" />
            </span>
            <!-- Content -->
            <div class="tw-p-3 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-200">
              <div class="tw-flex tw-justify-between tw-items-center">
                <h3 class="tw-text-sm tw-font-medium tw-text-gray-900">
                  {{ generateMessage(entry) }}
                </h3>
                <time class="tw-text-xs tw-font-normal tw-text-gray-400">
                  {{ formatDate(entry.created_at) }}
                </time>
              </div>
            </div>
          </li>
        </ol>
      </div>
    </div>
  </template>
  
  <script setup>
  import { defineProps, toRef } from 'vue';
  import { useI18n } from 'vue-i18n';
  
  const { t } = useI18n();
  const props = defineProps({
    data: { required: true },
    loading: { required: true, default: false },
  });
  const data = toRef(props, 'data');
  
  function formatDate(dateStr) {
    return new Date(dateStr).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  }
  
  function generateMessage(entry) {
    const name = entry.action_by || '—';
    const fields = entry.fields.map(f => f.field);
    const getField = (field) => entry.fields.find(f => f.field === field);
  
    if (fields.includes('agent_id')) return t('history.received', { name });
    if (fields.includes('agent_status')) return t(`history.agent_status.${getField('agent_status')?.new_value}`, { name });
    if (fields.includes('calls')) return t('history.call_attempt', { name, total: getField('calls')?.new_value });
    if (fields.includes('delivery_status')) return t(`history.delivery_status.${getField('delivery_status')?.new_value}`, { name });
    if (fields.includes('followup_id')) return t('history.followup_assigned', { name });
    if (fields.includes('customer_city') || fields.includes('customer_phone')) return t('history.contact_updated', { name });
  
    return t('history.updated', { name });
  }
  
  function getIcon(entry) {
    const fields = entry.fields.map(f => f.field);
    const getField = (field) => entry.fields.find(f => f.field === field)?.new_value;
  
    if (fields.includes('calls')) return { name: 'solar:phone-calling-bold', bg: 'tw-bg-blue-500' };
    if (fields.includes('agent_status')) {
      const status = getField('agent_status');
      if (status === 'confirmed') return { name: 'solar:check-circle-bold', bg: 'tw-bg-green-500' };
      if (status === 'no-answer') return { name: 'solar:phone-calling-bold', bg: 'tw-bg-yellow-500' };
      if (status === 'canceled') return { name: 'solar:close-circle-bold', bg: 'tw-bg-red-500' };
      return { name: 'iconamoon:edit-bold', bg: 'tw-bg-gray-500' };
    }
    if (fields.includes('delivery_status')) {
      const status = getField('delivery_status');
      if (status === 'delivered') return { name: 'heroicons-solid:check', bg: 'tw-bg-green-500' };
      if (status === 'settled') return { name: 'iconoir:hand-cash', bg: 'tw-bg-green-500' };
      if (status?.includes('return')) return { name: 'solar:refresh-bold', bg: 'tw-bg-orange-500' };
      return { name: 'ph:truck-bold', bg: 'tw-bg-blue-500' };
    }
    if (fields.includes('followup_id')) return { name: 'solar:user-plus-bold', bg: 'tw-bg-indigo-500' };
    if (fields.includes('agent_id')) return { name: 'solar:user-bold', bg: 'tw-bg-sky-500' };
    if (fields.includes('customer_city') || fields.includes('customer_phone')) return { name: 'solar:info-circle-bold', bg: 'tw-bg-purple-500' };
  
    return { name: 'solar:info-circle-bold', bg: 'tw-bg-gray-500' };
  }
  </script>
  